import type { PlaytestSession, PlaytestStage } from "./playtest-journey";

const storageVersion = 1;
const progressKeyPrefix = "lina.playtestProgress.v1";

export interface PlaytestProgressSnapshot {
  session: PlaytestSession;
  currentStage: PlaytestStage;
  completedStages: number[];
  stageScore: number;
  totalScore: number;
  stageCorrectCount: number;
  totalCorrectCount: number;
  currentQuestionIndex: number;
}

type StoredPlaytestProgress = PlaytestProgressSnapshot & {
  version: typeof storageVersion;
  updatedAt: string;
};

export function getPlaytestProgressKey(options: {
  userId?: string | null;
  islandSlug: string;
  stationId: string;
}) {
  return [
    progressKeyPrefix,
    options.userId || "anonymous",
    options.islandSlug,
    options.stationId,
  ].join(":");
}

export function saveStoredPlaytestProgress(
  storage: Storage,
  key: string,
  snapshot: PlaytestProgressSnapshot,
) {
  const payload: StoredPlaytestProgress = {
    ...snapshot,
    version: storageVersion,
    updatedAt: new Date().toISOString(),
  };

  storage.setItem(key, JSON.stringify(payload));
}

export function loadStoredPlaytestProgress(
  storage: Storage,
  key: string,
): PlaytestProgressSnapshot | null {
  const raw = storage.getItem(key);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredPlaytestProgress>;
    if (
      parsed.version !== storageVersion ||
      !parsed.session ||
      !parsed.currentStage
    )
      return null;

    return {
      session: parsed.session,
      currentStage: parsed.currentStage,
      completedStages: parsed.completedStages ?? [],
      stageScore: parsed.stageScore ?? parsed.session.stagePoints ?? 0,
      totalScore: parsed.totalScore ?? parsed.session.stationPoints ?? 0,
      stageCorrectCount: parsed.stageCorrectCount ?? 0,
      totalCorrectCount:
        parsed.totalCorrectCount ?? parsed.session.correctTotal ?? 0,
      currentQuestionIndex:
        parsed.currentQuestionIndex ?? parsed.session.history?.length ?? 0,
    };
  } catch {
    return null;
  }
}

export function clearStoredPlaytestProgress(storage: Storage, key: string) {
  storage.removeItem(key);
}
